import React, { useEffect, useMemo, useState } from "react";
import { Box, Container, useTheme, useMediaQuery, CircularProgress, Typography } from "@mui/material";
import TopBar from './components/TopBar';
import Filters from './components/Filters';
import ProductGrid from './components/ProductGrid';
import ProductList from './components/ProductList';
import QuickView from './components/QuickView';
import OrderDialog from './components/OrderDialog';
import FilterDrawer from './components/FilterDrawer';
import Paginator from './components/Paginator';
import Footer from './components/Footer';
import { loadFilesFromPaths, buildProducts } from './utils/dataUtils';

// ---------- config: add your file paths here ----------
const FILE_PATHS = [
  // "/files/bioderma_products.csv",
  "/files/cetaphil_products.csv",
  "/files/bioderma_products.csv"
];

// Loading Component
const LoadingScreen = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
      gap: 2
    }}
  >
    <CircularProgress size={48} />
    <Typography variant="h6" color="text.secondary">
      Loading products...
    </Typography>
    <Typography variant="body2" color="text.disabled">
      Please wait while we fetch the latest data
    </Typography>
  </Box>
);

export default function App() {
  const [allProducts, setAllProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [view, setView] = useState("grid");
  const [maxPriceUsd, setMaxPriceUsd] = useState(1000);
  const [priceMaxUsd, setPriceMaxUsd] = useState(1000);
  const [quick, setQuick] = useState(null);
  const [orderOpen, setOrderOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const pageSize = 16;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isLaptop = useMediaQuery(theme.breakpoints.between('md', 'xl'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // Load files from FILE_PATHS on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        let products = [];
        
        if (FILE_PATHS && FILE_PATHS.length) {
          products = await loadFilesFromPaths(FILE_PATHS);
        }
        
        if (!products.length) {
          // Demo fallback if no files configured
          const demo = [
            { Handle: "prod-8", Title: "Gentle Eye Cream", Vendor: "Brand G", Type: "Eye Care", Tags: "Eye Care, Anti-Aging, Trending", "Body (HTML)": "Delicate eye cream for fine lines and dark circles.", "Variant Price": "24.99", "Variant Inventory Qty": 7, "Image Src": "https://placehold.co/800x600?text=Product+8" },
          ];
          products = buildProducts(demo);
        }
        
        setAllProducts(products);
        
        // Set default "Popular" filter on initial load only
        if (isInitialLoad) {
          // Always try to filter by "Popular" tag by default
          const allTags = products.flatMap(p => p.tags).filter(Boolean);
          const defaultTagName = 'Popular'; // Choose "Popular" as the default
          
          // Find the exact case-sensitive "Popular" tag from the products
          const exactTag = allTags.find(t => t.toLowerCase() === defaultTagName.toLowerCase());
          
          if (exactTag) {
            setSelectedTags([exactTag]);
          }
          setIsInitialLoad(false);
        }
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isInitialLoad]);

  // Derived lists
  const brands = useMemo(() => Array.from(new Set(allProducts.map(p => p.brand))).sort(), [allProducts]);
  const categories = useMemo(() => Array.from(new Set(allProducts.map(p => p.category))).sort(), [allProducts]);
  const tags = useMemo(() => Array.from(new Set(allProducts.flatMap(p => p.tags))).filter(Boolean).sort(), [allProducts]);
  const brandCounts = useMemo(
    () => allProducts.reduce((acc, p) => {
      acc[p.brand] = (acc[p.brand] || 0) + 1;
      return acc;
    }, {}),
    [allProducts]
  );
  
  const categoryCounts = useMemo(
    () => allProducts.reduce((acc, p) => {
      acc[p.category] = (acc[p.category] || 0) + 1;
      return acc;
    }, {}),
    [allProducts]
  );

  const maxUsd = useMemo(() => { 
    const m = Math.max(0, ...allProducts.map(p => (p.priceUsd || 0))); 
    return Math.ceil(m || 1000); 
  }, [allProducts]);
  
  useEffect(() => { 
    setMaxPriceUsd(maxUsd); 
    setPriceMaxUsd(maxUsd); 
  }, [maxUsd]);

  // Filtering logic - Changed to use OR condition for tags
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allProducts.filter(p => {
      const matchesQ = !q || [p.name, p.brand, p.category, p.tags.join(" ")].some(v => (v||"").toLowerCase().includes(q));
      const matchesBrand = !selectedBrand || p.brand === selectedBrand;
      const matchesCat = !selectedCategory || p.category === selectedCategory;
      // Changed: OR condition for tags instead of AND
      const matchesTags = !selectedTags.length || selectedTags.some(t => p.tags.includes(t));
      const matchesPrice = p.priceUsd == null || p.priceUsd <= priceMaxUsd + 0.0001;
      return matchesQ && matchesBrand && matchesCat && matchesTags && matchesPrice;
    });
  }, [allProducts, query, selectedBrand, selectedCategory, selectedTags, priceMaxUsd]);

  // Pagination
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => setPage(1), [query, selectedBrand, selectedCategory, selectedTags, priceMaxUsd, view]);
  const pageItems = filtered.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);

  const filterProps = {
    brands,
    categories,
    selectedBrand,
    setSelectedBrand,
    selectedCategory,
    setSelectedCategory,
    maxPriceUsd,
    priceMaxUsd,
    setPriceMaxUsd,
    tags,
    selectedTags,
    setSelectedTags,
    view,
    setView
  };
  
  // Enhanced filter width calculation
  const getFilterWidth = () => {
    if (isMobile) return 280;
    if (isTablet) return 350;
    if (isLaptop) return 350;
    return 350; // Desktop and larger screens
  };

  const filterWidth = getFilterWidth();

  // Show loading screen while data is being loaded
  if (isLoading) {
    return (
      <Box sx={{ 
        minHeight: "100vh", 
        bgcolor: "background.default",
        display: 'flex',
        flexDirection: 'column'
      }}>
        <TopBar 
          brandCounts={{}} 
          categoryCounts={{}} 
          total={0} 
          query={query} 
          setQuery={setQuery} 
          onOpenOrder={() => setOrderOpen(true)}
          onOpenFilters={() => setFilterDrawerOpen(true)}
        />
        <Container maxWidth={false} sx={{ flex: 1 }}>
          <LoadingScreen />
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: "100vh", 
      bgcolor: "background.default",
      display: 'flex',
      flexDirection: 'column'
    }}>
      <TopBar 
        brandCounts={brandCounts} 
        categoryCounts={categoryCounts} 
        total={allProducts.length} 
        query={query} 
        setQuery={setQuery} 
        onOpenOrder={() => setOrderOpen(true)}
        onOpenFilters={() => setFilterDrawerOpen(true)}
      />

      {/* Wider Container for better space utilization */}
      <Container 
        maxWidth={false} 
        sx={{ 
          py: { xs: 1, sm: 2 }, 
          px: { xs: 1, sm: 2, md: 3, lg: 4 },
          maxWidth: { xs: '100%', md: '1400px', lg: '1600px', xl: '1800px' },
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {isMobile ? (
          // Mobile Layout: Stacked
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                Showing {filtered.length} of {allProducts.length} products
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <button onClick={() => setView("grid")} style={{ background: 'none', border: 'none' }}>
                  Grid
                </button>
                <button onClick={() => setView("list")} style={{ background: 'none', border: 'none' }}>
                  List
                </button>
              </Box>
            </Box>

            <Box sx={{ flex: 1 }}>
              {view === 'grid' ? (
                <ProductGrid products={pageItems} onQuickView={setQuick} isMobile />
              ) : (
                <ProductList products={pageItems} onQuickView={setQuick} isMobile />
              )}
            </Box>

            <Paginator page={page} setPage={setPage} pages={pages} />
          </Box>
        ) : (
          // Desktop Layout: Wider Sticky Sidebar + Content
          <Box sx={{ 
            display: 'flex', 
            gap: { md: 3, lg: 4, xl: 5 }, 
            alignItems: 'flex-start',
            flex: 1
          }}>
            {/* LEFT: Enhanced Sticky Filters */}
            <Box sx={{ 
              width: filterWidth, 
              flex: `0 0 ${filterWidth}px`,
              position: 'sticky',
              top: 20,
              alignSelf: 'flex-start',
              // Removed maxHeight and overflowY for natural expansion
              '&::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: '1px',
                backgroundColor: 'divider',
                opacity: 0.1
              }
            }}>
              <Filters {...filterProps} />
            </Box>

            {/* RIGHT: Products - More spacious content area */}
            <Box sx={{ 
              flex: 1, 
              minWidth: 0,
              ml: { xs:1, md: 1, lg: 2, xl: 3 }, // Add margin for larger screens
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box variant="body2" color="text.secondary">
                  Showing {filtered.length} of {allProducts.length} products
                </Box>
              </Box>

              <Box sx={{ flex: 1 }}>
                {view === 'grid' ? (
                  <ProductGrid products={pageItems} onQuickView={setQuick} />
                ) : (
                  <ProductList products={pageItems} onQuickView={setQuick} />
                )}
              </Box>

              <Paginator page={page} setPage={setPage} pages={pages} />
            </Box>
          </Box>
        )}
      </Container>

      <Footer />

      {/* Mobile Filter Drawer */}
      <FilterDrawer 
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        {...filterProps}
      />

      <QuickView product={quick} onClose={() => setQuick(null)} />
      <OrderDialog open={orderOpen} onClose={() => setOrderOpen(false)} />
    </Box>
  );
}